import styled from 'styled-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { Platform, DeviceType, Orientation, WriteDirection } from 'sofa-reborn-react';

type Attributes = {
	platform: Platform;
	orientation: Orientation;
	deviceType: DeviceType;
	writeDirection: WriteDirection;
	coverId?: number;
	borderColor?: string;
	hasArmrestPicker?: boolean;
	selected?: boolean;
};

function getCoverPickerButtonStyling(props: Attributes): string {
	if (props.platform === 'kiosk') {
		return `
			height: 108px;
			width: 370px;
			padding: 30px;
			border-bottom: 1px solid ${SkapaTheme.color.border.light};
			flex-direction: column;
		`;
	}

	switch (props.deviceType) {
		case 'mobile': {
			return props.orientation === 'portrait'
				? `
						height: ${props.hasArmrestPicker ? 'fit-content' : '110px'};
						width: 50px;
						border-right: ${
							props.writeDirection === 'rtl' || props.hasArmrestPicker
								? 'none'
								: '1px solid' + SkapaTheme.color.border.light
						};
						border-left: ${
							props.writeDirection === 'rtl' && !props.hasArmrestPicker
								? '1px solid' + SkapaTheme.color.border.light
								: 'none'
						};
						flex-direction: column;
						justify-content: center;
						padding-right: ${props.writeDirection === 'rtl' ? '0' : '8px'};
						padding-left: ${props.writeDirection === 'rtl' ? '8px' : '0'};
						margin-top: ${props.hasArmrestPicker ? '8px' : '0'};
				  `
				: `
						height: 55px;
						justify-content: center;
						align-items: center;
				  `;
		}
		case 'tablet': {
			return `
				width: ${props.orientation === 'portrait' ? '70px' : '100px'};
				height: ${props.orientation === 'portrait' && props.hasArmrestPicker ? 'auto' : '150px'};
				border-right: ${
					props.hasArmrestPicker || props.writeDirection === 'rtl'
						? 'none'
						: '1px solid' + SkapaTheme.color.border.light
				};
				border-left: ${
					!props.hasArmrestPicker && props.writeDirection === 'rtl'
						? '1px solid' + SkapaTheme.color.border.light
						: 'none'
				};
				flex-direction: ${props.orientation === 'portrait' && props.hasArmrestPicker ? 'row' : 'column'};
				justify-content: center;
				padding-right: ${props.writeDirection === 'rtl' ? '0' : '8px'};
				padding-left: ${props.writeDirection === 'rtl' ? '8px' : '0'};
			`;
		}
		default: {
			return `
				border-bottom: 1px solid ${SkapaTheme.color.border.light};
				justify-content: space-evenly;
				flex-direction: column;
				padding: ${SkapaTheme.spacing.space100}px ${SkapaTheme.spacing.space150}px;
			`;
		}
	}
}

function getCoversStyling(props: Attributes): string {
	if (props.platform === 'kiosk') {
		return `
			width: 100%;
			justify-content: flex-start;
		`;
	}

	switch (props.deviceType) {
		case 'mobile': {
			return props.orientation === 'portrait'
				? `
						margin-top: 4px;
				  `
				: `
						width: 70px;
						height: 40px;
				  `;
		}
		case 'tablet': {
			return `
				margin-top: 8px;
			`;
		}
		case 'desktop': {
			return `
				justify-content: flex-start;
				width: 100%;
			`;
		}
		default: {
			return ``;
		}
	}
}

function getCoverStyling(props: Attributes): string {
	if (props.platform === 'kiosk') {
		return `
			width: ${props.borderColor || props.selected ? '38px' : '40px'};
			height: ${props.borderColor || props.selected ? '38px' : '40px'};
			cursor: pointer;
			margin-left: ${
				props.coverId && props.coverId > 0 ? (props.writeDirection === 'rtl' ? '0' : '12px') : '0'
			};
			margin-right: ${
				props.coverId && props.coverId > 0 ? (props.writeDirection === 'rtl' ? '12px' : '0') : '0'
			};
		`;
	}

	switch (props.deviceType) {
		case 'mobile': {
			return props.orientation === 'portrait'
				? `
						width: ${props.borderColor ? '10px' : '12px'};
						height: ${props.borderColor ? '10px' : '12px'};
						margin: ${props.writeDirection === 'rtl' ? '4px 4px 0 0' : '4px 0 0 4px'};
				  `
				: `
						width: ${props.borderColor ? '10px' : '12px'};
						height: ${props.borderColor ? '10px' : '12px'};
						margin-left: ${props.writeDirection === 'rtl' ? '0' : '4px'};
						margin-right: ${props.writeDirection === 'rtl' ? '4px' : '0'};
				  `;
		}
		case 'tablet': {
			return `
				width: ${props.borderColor ? '14px' : '16px'};
				height: ${props.borderColor ? '14px' : '16px'};
				margin: ${props.writeDirection === 'rtl' ? '4px 4px 0 0' : '4px 0 0 4px'};
			`;
		}
		default: {
			return `
				width: ${props.borderColor || props.selected ? '28px' : '30px'};
				height: ${props.borderColor || props.selected ? '28px' : '30px'};
				cursor: pointer;
				margin-left: ${
					props.coverId && props.coverId > 0 ? (props.writeDirection === 'rtl' ? '0' : '12px') : '0'
				};
				margin-right: ${
					props.coverId && props.coverId > 0 ? (props.writeDirection === 'rtl' ? '12px' : '0') : '0'
				};
			`;
		}
	}
}

function getSelectedCoverStyling(
	deviceType: DeviceType,
	platform: Platform,
	orientation: Orientation,
	writeDirection: WriteDirection,
	borderColor: string
): string {
	if (platform === 'kiosk') {
		return `
			width: ${borderColor ? '60.4px' : '62.4px'};
			height: ${borderColor ? '60.4px' : '62.4px'};
		`;
	}

	switch (deviceType) {
		case 'mobile': {
			return orientation === 'portrait'
				? `
						width: ${borderColor ? '38px' : '40px'};
						height: ${borderColor ? '38px' : '40px'};
						margin-left: ${writeDirection === 'rtl' ? '0' : '4px'};
						margin-right: ${writeDirection === 'rtl' ? '4px' : '0'};
				  `
				: `
						width: ${borderColor ? '30px' : '32px'};
						height: ${borderColor ? '30px' : '32px'};
				  `;
		}
		case 'tablet': {
			return `
				width: ${borderColor ? '46px' : '48px'};
				height: ${borderColor ? '46px' : '48px'};
			`;
		}
		case 'desktop': {
			return `
				width: ${borderColor ? '34px' : '36px'};
				height: ${borderColor ? '34px' : '36px'};
			`;
		}

		default: {
			return `
				width: ${borderColor ? '38px' : '40px'};
				height: ${borderColor ? '38px' : '40px'};
			`;
		}
	}
}

export const StyledCoverPickerButton = styled.div<{
	platform: Platform;
	orientation: Orientation;
	deviceType: DeviceType;
	writeDirection: WriteDirection;
	hasArmrestPicker: boolean;
}>`
	display: flex;
	cursor: pointer;
	background-color: ${SkapaTheme.color.neutral.white};
	${(props) => getCoverPickerButtonStyling(props)}
`;

export const SelectedCoverWrapper = styled.div<{
	isTablet: boolean;
	isMobile: boolean;
}>`
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${(props) => (props.isTablet ? '70px' : props.isMobile ? '44px' : '48px')};
`;

export const SelectedCover = styled.div<{
	deviceType: DeviceType;
	platform: Platform;
	orientation: Orientation;
	writeDirection: WriteDirection;
	color: string;
	borderColor: string;
}>`
	background-color: ${(props) => props.color};
	border-radius: 50%;
	border: ${(props) => (props.borderColor ? '1px solid ' + props.borderColor : 'none')};
	${(props) =>
		getSelectedCoverStyling(
			props.deviceType,
			props.platform,
			props.orientation,
			props.writeDirection,
			props.borderColor
		)};
`;

export const Covers = styled.div<{
	deviceType: DeviceType;
	platform: Platform;
	orientation: Orientation;
	writeDirection: WriteDirection;
}>`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	${(props) => getCoversStyling(props)};
`;

export const Cover = styled.div<{
	coverId: number;
	orientation: Orientation;
	deviceType: DeviceType;
	platform: Platform;
	writeDirection: WriteDirection;
	color: string;
	borderColor: string;
	selected: boolean;
}>`
	${(props) => getCoverStyling(props)}
	border-radius: 50%;
	background-color: ${(props) => props.color};
	border: ${(props) =>
		props.selected
			? '2px solid #ffffff'
			: props.borderColor
			? '1px solid ' + props.borderColor
			: 'none'};
	box-shadow: ${(props) =>
		props.selected ? '0 0 0 2px ' + SkapaTheme.color.semantic.informative : 'none'};
`;

export const PlusIconWrapper = styled.div<{
	orientation: Orientation;
	deviceType: DeviceType;
	platform: Platform;
	writeDirection: WriteDirection;
	coverId: number;
}>`
	${(props) => getCoverStyling(props)}
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: ${(props) =>
		props.deviceType !== 'mobile' && props.deviceType !== 'tablet' ? '50%' : '0'};
	border: ${(props) =>
		props.deviceType !== 'mobile' && props.deviceType !== 'tablet'
			? '1px solid' + SkapaTheme.color.border.dark
			: 'none'};
`;

export const IconWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
`;

export const CoverIconContainer = styled.div<{
	isKiosk: boolean;
}>`
	display: flex;
	flex-direction: row;
	padding-bottom: ${(props) => (props.isKiosk ? '16px' : '8px')};
`;

export const CoverContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

export const IconContainer = styled.div`
	display: flex;
	flex: 1;
	height: 100%;
	justify-content: flex-end;
	align-items: center;
	margin-inline-end: -10px;
`;
